import { Controller, type Control, type FieldValues, type Path, type RegisterOptions } from 'react-hook-form';
import {
  Autocomplete,
  type AutocompleteProps,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  type SelectProps,
  TextField,
} from '@mui/material';

type Option = {
  label: string;
  value: string;
};

/**
 * Reusable Autocomplete-backed select controlled by react-hook-form's Controller.
 * Supports typing/searching by default. For short fixed lists where typing is
 * undesirable (e.g., a 2-3 option enum inside a Drawer), pass `native` to
 * render a plain MUI `Select` instead — this is also the right choice when
 * the field lives inside a portal'd surface that needs a higher menu z-index
 * (pass `slotProps={{ menu: { sx: { zIndex: 2200 } } }}`).
 *
 * Example:
 * <FormSelectField control={control} name="state" label="State" options={...} />
 * <FormSelectField control={control} name="discountType" label="Type" options={...} native disableClearable />
 */
type CommonProps<TFieldValues extends FieldValues, TOption extends Option> = {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  helperText?: string;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  options: TOption[];
  label?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  fullWidth?: boolean;
  onSelect?: (option: TOption) => void;
};

type AutocompleteVariantProps<
  TFieldValues extends FieldValues,
  TOption extends Option,
  DisableClearable extends boolean | undefined = false,
> = CommonProps<TFieldValues, TOption> & {
  native?: false;
  /** Prevent typing into the input while still allowing dropdown selection. */
  readOnly?: boolean;
} & Omit<
    AutocompleteProps<TOption, false, DisableClearable, false>,
    'name' | 'defaultValue' | 'value' | 'onChange' | 'inputRef' | 'renderInput' | 'onSelect' | 'options' | 'size'
  >;

type NativeVariantProps<TFieldValues extends FieldValues, TOption extends Option> = CommonProps<
  TFieldValues,
  TOption
> & {
  /** Render a plain MUI Select (no typing/searching). Required for short enum lists. */
  native: true;
  /** Forwarded to the underlying Select's MenuProps — useful for z-index in portal'd surfaces. */
  MenuProps?: SelectProps['MenuProps'];
  /** When true, treat the first option as the fallback for null/undefined values. */
  disableClearable?: boolean;
};

type FormSelectFieldProps<
  TFieldValues extends FieldValues,
  TOption extends Option,
  DisableClearable extends boolean | undefined = false,
> = AutocompleteVariantProps<TFieldValues, TOption, DisableClearable> | NativeVariantProps<TFieldValues, TOption>;

export function FormSelectField<
  TFieldValues extends FieldValues,
  TOption extends Option,
  DisableClearable extends boolean | undefined = false,
>(props: FormSelectFieldProps<TFieldValues, TOption, DisableClearable>) {
  if (props.native) {
    return <NativeSelectField {...props} />;
  }
  return <AutocompleteSelectField {...(props as AutocompleteVariantProps<TFieldValues, TOption, DisableClearable>)} />;
}

function NativeSelectField<TFieldValues extends FieldValues, TOption extends Option>(
  props: NativeVariantProps<TFieldValues, TOption>
) {
  const {
    name,
    control,
    rules,
    label,
    size,
    options,
    onSelect,
    disabled,
    fullWidth = true,
    MenuProps,
    disableClearable,
  } = props;
  const labelId = `${String(name)}-label`;
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const fallback =
          disableClearable && (field.value === null || field.value === undefined) ? options[0]?.value : field.value;
        return (
          <FormControl fullWidth={fullWidth} size={size} disabled={disabled} error={Boolean(fieldState.error)}>
            {label && <InputLabel id={labelId}>{label}</InputLabel>}
            <Select
              {...field}
              labelId={labelId}
              label={label}
              value={fallback ?? ''}
              MenuProps={MenuProps}
              onChange={(e) => {
                const next = e.target.value;
                field.onChange(next);
                const matched = options.find((o) => o.value === next);
                if (matched) {
                  onSelect?.(matched);
                }
              }}
            >
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {fieldState.error?.message && <FormHelperText>{fieldState.error.message}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
}

function AutocompleteSelectField<
  TFieldValues extends FieldValues,
  TOption extends Option,
  DisableClearable extends boolean | undefined = false,
>(props: AutocompleteVariantProps<TFieldValues, TOption, DisableClearable>) {
  const {
    name,
    control,
    options,
    rules,
    label,
    size,
    onSelect,
    readOnly,
    native: _native,
    ...selectFieldProps
  } = props;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const matchedOption = options.find((option) => option.value === field.value) ?? null;
        // When the field is non-clearable, MUI requires a non-null value. If the form
        // field has no match (e.g. unhydrated, null from a nullable column), fall back
        // to the first option so MUI doesn't render the "value not in options" warning.
        // Callers using disableClearable should hydrate a default themselves.
        const selectedOption =
          (selectFieldProps as AutocompleteProps<TOption, false, DisableClearable, false>).disableClearable &&
          matchedOption == null
            ? (options[0] ?? null)
            : matchedOption;

        return (
          <Autocomplete
            {...(selectFieldProps as AutocompleteProps<TOption, false, DisableClearable, false>)}
            // The runtime fallback above guarantees `selectedOption` is non-null when
            // `disableClearable` is set. TS can't narrow against the conditional generic,
            // so we cast to the union MUI's `AutocompleteValue` produces.
            value={selectedOption as DisableClearable extends true ? TOption : TOption | null}
            fullWidth
            options={options}
            getOptionLabel={(opt) => opt.label}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={field.ref}
                label={label}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                onChange={field.onChange}
                value={field.value}
                size={size}
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    readOnly,
                  },
                }}
              />
            )}
            onChange={(_, option) => {
              if (!option) {
                field.onChange(null);
                return;
              }

              field.onChange(option.value);
              onSelect?.(option);
            }}
          />
        );
      }}
    />
  );
}
